import { useCallback, useEffect, useId, useSyncExternalStore } from "react";
import { cn } from "@-/com.npmjs.cn";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Progress } from "@-/dev.improve.haniverse.components.ui.progress";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  getSentenceSpeechState,
  subscribeToSentenceSpeech,
} from "@-/dev.improve.haniverse.lib.sentence.speech";
import {
  NEURAL_SENTENCE_VOICES,
  type NeuralSentenceVoice,
  SYSTEM_SENTENCE_VOICE_ID,
  type SentenceVoiceId,
} from "@-/dev.improve.haniverse.lib.sentence.speech.voice-catalog";
import {
  canUseNeuralSentenceVoices,
  getNeuralSentenceVoiceDownloadBytes,
  isNeuralSentenceVoiceInstalled,
  startNeuralSentenceVoiceDownload,
  useNeuralSentenceVoiceDownload,
  useSentenceVoiceFilesRevision,
} from "@-/dev.improve.haniverse.lib.sentence.speech.voices";
import { formatDownloadSize } from "#format-download-size";
import { previewSentenceVoiceSample } from "#sentence-voice-preview";

/** Recommended voices first, then the rest in catalog order. */
function orderedNeuralVoices(): NeuralSentenceVoice[] {
  return [
    ...NEURAL_SENTENCE_VOICES.filter((voice) => voice.recommended),
    ...NEURAL_SENTENCE_VOICES.filter((voice) => !voice.recommended),
  ];
}

function usePreviewPlaying(ownerId: string): boolean {
  const state = useSyncExternalStore(subscribeToSentenceSpeech, getSentenceSpeechState, () => null);
  return state?.ownerId === ownerId;
}

function PreviewButton({ voiceId }: { voiceId: SentenceVoiceId }) {
  const ownerId = useId();
  const isPlaying = usePreviewPlaying(ownerId);
  return (
    <Pressable
      accessibilityLabel={isPlaying ? "Stop voice sample" : "Play voice sample"}
      accessibilityRole="button"
      className={cn(
        "size-9 items-center justify-center rounded-full",
        isPlaying ? "bg-primary/10" : "bg-muted/60",
      )}
      hitSlop={4}
      onPress={() => {
        previewSentenceVoiceSample({ ownerId, voiceId });
      }}
      testID={`sentence-voice-preview-${voiceId}`}
    >
      <Icon
        className={isPlaying ? "text-primary" : "text-muted-foreground"}
        name="IoVolumeHighOutline"
        size={18}
      />
    </Pressable>
  );
}

function OptionRow({
  description,
  isSelected,
  label,
  onPress,
  status,
  testID,
  voiceId,
}: {
  description: string;
  isSelected: boolean;
  label: string;
  onPress: () => void;
  status: React.ReactNode;
  testID: string;
  voiceId: SentenceVoiceId;
}) {
  return (
    <View className="flex-row items-center gap-x-3 px-4 py-3">
      <Pressable
        accessibilityRole="radio"
        accessibilityState={{ checked: isSelected }}
        className="min-w-0 flex-1 flex-row items-center gap-x-3 active:opacity-70"
        onPress={onPress}
        testID={testID}
      >
        <View className="min-w-0 flex-1 gap-y-0.5">
          <Text className="text-base text-foreground">{label}</Text>
          <Text className="text-xs text-muted-foreground">{description}</Text>
          {status}
        </View>
        <View className="size-6 items-center justify-center">
          {isSelected ? <Icon className="text-primary" name="MdCheck" size={22} /> : null}
        </View>
      </Pressable>
      <PreviewButton voiceId={voiceId} />
    </View>
  );
}

function NeuralVoiceRow({
  isSelected,
  onChoose,
  onDownloadProgress,
  voice,
}: {
  isSelected: boolean;
  onChoose: (voiceId: SentenceVoiceId) => void;
  onDownloadProgress?: () => void;
  voice: NeuralSentenceVoice;
}) {
  // Re-read the synchronous install check whenever files change.
  useSentenceVoiceFilesRevision();
  const download = useNeuralSentenceVoiceDownload(voice.id);
  const isInstalled = isNeuralSentenceVoiceInstalled(voice);
  const missingBytes = getNeuralSentenceVoiceDownloadBytes(voice);

  // Each progress step tells the waiting sentence the download is alive.
  useEffect(() => {
    if (download.status === "downloading") {
      onDownloadProgress?.();
    }
  }, [download, onDownloadProgress]);

  const choose = useCallback(() => {
    if (isNeuralSentenceVoiceInstalled(voice)) {
      onChoose(voice.id);
      return;
    }
    if (download.status === "downloading") {
      return;
    }
    startNeuralSentenceVoiceDownload(voice).then(
      () => {
        onChoose(voice.id);
      },
      () => {
        // The row shows the failure and offers a retry.
      },
    );
  }, [download.status, onChoose, voice]);

  let status: React.ReactNode = null;
  if (download.status === "downloading") {
    const percent = Math.round(download.fraction * 100);
    status = (
      <View className="mt-1.5 gap-y-1">
        <Progress className="h-1.5" value={percent} />
        <Text className="text-xs text-muted-foreground">Downloading… {percent}%</Text>
      </View>
    );
  } else if (download.status === "failed") {
    status = (
      <Text className="mt-1 text-xs text-destructive">
        Download failed. Tap to try again. ({download.message})
      </Text>
    );
  } else if (!isInstalled) {
    status = (
      <Text className="mt-1 text-xs text-muted-foreground">
        Tap to download · {formatDownloadSize(missingBytes)}
      </Text>
    );
  }

  return (
    <OptionRow
      description={`${voice.recommended ? "Recommended · " : ""}${voice.description}`}
      isSelected={isSelected}
      label={voice.name}
      onPress={choose}
      status={status}
      testID={`sentence-voice-option-${voice.id}`}
      voiceId={voice.id}
    />
  );
}

/**
 * Every sentence voice: the downloadable natural voices (recommended first) and the system voice.
 * Choosing a voice that is not downloaded yet starts its download and only reports the choice once
 * it can speak. Each row has a sample button, so the learner can hear a voice before downloading.
 */
export function SentenceVoiceOptionList({
  onChoose,
  onDownloadProgress,
  selectedVoiceId,
}: {
  /** The learner picked a voice that is ready to speak. */
  onChoose: (voiceId: SentenceVoiceId) => void;
  /** Called while a chosen voice downloads (keeps a waiting sentence from timing out). */
  onDownloadProgress?: () => void;
  selectedVoiceId: SentenceVoiceId | null;
}) {
  // Natural voices need the native synthesizer; the web offers only the system voice.
  const neuralVoices = canUseNeuralSentenceVoices() ? orderedNeuralVoices() : [];
  return (
    <View>
      {neuralVoices.map((voice) => (
        <View key={voice.id}>
          <NeuralVoiceRow
            isSelected={selectedVoiceId === voice.id}
            onChoose={onChoose}
            onDownloadProgress={onDownloadProgress}
            voice={voice}
          />
          <View className="ml-4 h-px bg-border" />
        </View>
      ))}
      <OptionRow
        description="No download. Sound quality depends on your device."
        isSelected={selectedVoiceId === SYSTEM_SENTENCE_VOICE_ID}
        label="System voice"
        onPress={() => {
          onChoose(SYSTEM_SENTENCE_VOICE_ID);
        }}
        status={null}
        testID="sentence-voice-option-system"
        voiceId={SYSTEM_SENTENCE_VOICE_ID}
      />
    </View>
  );
}
